import Papa from 'papaparse';

interface CsvVariant {
  id: string;
  variant: string;
  index: number;
  price: number;
}

interface CsvCustomization {
  name: string;
  price: number;
}

interface TmpVariant {
  size: string;
  color: string;
  price: number;
}

interface CsvProduct {
  name: string;
  description: string;
  price: number;
  vendor: string;
  collection: string;
  sizes: string[];
  colors: string[];
  variants: CsvVariant[];
  tags: string[];
  customizations: CsvCustomization[];
  tmp?: TmpVariant[];
}

interface ParseResult {
  error: boolean;
  message?: unknown;
  data?: CsvProduct[];
}

function parseProductsFromCSV(csvText: string): ParseResult {
  const result: ParseResult = {
    error: true,
  };

  const { data, meta } = Papa.parse<Record<string, string>>(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.trim(),
    transform: (v, _h) => v.trim(),
  });

  // validate the fields
  const REQUIRED_FIELDS = [
    'ProductId',
    'Name',
    // 'Description',
    'Price',
    'Vendor',
    'Collection',
    'Size',
    'Color',
    'Variant Price',
    // 'Customization 1',
    // 'Customization 2',
    // 'Tags'
  ];
  if (!REQUIRED_FIELDS.every((rf) => meta.fields!.includes(rf))) {
    result.message = 'missing required fields';
    return result;
  }

  // compose products
  const products: CsvProduct[] = [];
  for (let i = 0; i < data.length; i += 1) {
    const current = data[i];
    if (!current) {
      continue;
    }

    if (!current.ProductId) {
      continue;
    }

    // all required field except "ProductId" should exist
    if (REQUIRED_FIELDS.slice(1).some((rf) => !current[rf])) {
      result.message = `data error on line: ${i + 2}`;
      return result;
    }

    let exists = products.find((prod) => prod.name === current.Name);
    if (!exists) {
      const tags = current.Tags
        ? current.Tags.split(',').filter((t) => t).map((t) => t.trim())
        : [];

      exists = {
        name: current.Name!,
        description: current.Description || '',
        price: parseInt(current.Price!),
        vendor: current.Vendor!,
        collection: current.Collection!,
        sizes: [],
        colors: [],
        variants: [],
        tags,
        customizations: Object.keys(current).reduce<CsvCustomization[]>((acc, key) => {
          if (key.toLowerCase().startsWith('customization')) {
            const parts = current[key]!.split(',');
            acc.push({
              name: parts[0]!,
              price: parseInt(parts[1] as string),
            });
          }
          return acc;
        }, []),
        tmp: [],
      };
      products.push(exists);
    }

    exists.sizes.push(current.Size!);
    exists.colors.push(current.Color!);
    exists.tmp!.push({
      size: current.Size!,
      color: current.Color!,
      price: parseInt(current['Variant Price']!),
    });
  }

  // compose variants
  try {
    products.forEach((prod) => {
      prod.sizes = [...new Set(prod.sizes)];
      prod.colors = [...new Set(prod.colors)];

      let i = 0;
      prod.colors.forEach((c) => {
        prod.sizes.forEach((s) => {
          const id = `${s} / ${c}`;
          const find = prod.tmp!.find((v) => id === `${v.size} / ${v.color}`)
          if (!find) {
            throw new Error(`variant not find for ${s} / ${c} of ${prod.name}`);
          }
          // const price = find ? find.price : -1;
          prod.variants.push({
            id,
            variant: id,
            index: i,
            price: find.price,
          });
          i += 1;
        });
      });

      delete prod.tmp;
    });
  } catch (e) {
    result.message = e instanceof Error ? e.message : String(e);
    return result;
  }

  result.data = products;
  result.error = false;
  return result;
}

export {
  parseProductsFromCSV,
};
